import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { postCaptacionMultipart } from "../services/captacionUpload";
import { nombreParaPlantillaMensaje } from "../utils/nombrePlantillaMensaje";

const MAX_ARCHIVOS_RESPUESTA = 6;
const MAX_MB_POR_ARCHIVO = 20;

function esImagen(mime) {
  return String(mime || "").startsWith("image/");
}

function AdjuntosGrid({ adjuntos, border, txtMuted, isDark }) {
  if (!adjuntos?.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0" }}>
      {adjuntos.map((a) => (
        <a
          key={a.id}
          href={a.url}
          target="_blank"
          rel="noreferrer"
          title={a.nombre_original || "adjunto"}
          style={{
            width: 68,
            height: 68,
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${border}`,
            background: isDark ? "#111827" : "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            textDecoration: "none",
          }}
        >
          {esImagen(a.mime) ? (
            <img src={a.url} alt={a.nombre_original || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <>
              <span style={{ fontSize: 20 }}>{a.mime === "application/pdf" ? "📄" : "📎"}</span>
              <span style={{ fontSize: 8, color: txtMuted, padding: "0 3px", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                {a.nombre_original || "adjunto"}
              </span>
            </>
          )}
        </a>
      ))}
    </div>
  );
}

const CATEGORIA_LABEL = { error: "Error / fallo", duda: "Duda", mejora: "Mejora", otro: "Otro" };
const PRIORIDAD_LABEL = { baja: "Baja", media: "Media", alta: "Alta", urgente: "Urgente" };
const ESTADO_LABEL = { abierto: "Abierto", en_proceso: "En proceso", resuelto: "Resuelto", cerrado: "Cerrado" };

const ESTADO_COLOR = {
  abierto: { fg: "#b91c1c", bg: "#fee2e2" },
  en_proceso: { fg: "#92400e", bg: "#fef3c7" },
  resuelto: { fg: "#065f46", bg: "#d1fae5" },
  cerrado: { fg: "#334155", bg: "#e2e8f0" },
};

const PRIORIDAD_COLOR = {
  baja: "#64748b",
  media: "#2563eb",
  alta: "#d97706",
  urgente: "#dc2626",
};

function fechaCorta(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

/** Tiempo desde creación hasta resolución (min / h / d). */
function duracionResolucion(createdAt, resolvedAt) {
  if (!createdAt || !resolvedAt) return "";
  const ms = new Date(resolvedAt).getTime() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const minutos = Math.floor(ms / 60000);
  if (minutos < 60) return `${Math.max(1, minutos)} min`;
  const horas = Math.floor(minutos / 60);
  const minRest = minutos % 60;
  if (horas < 24) return minRest ? `${horas} h ${minRest} min` : `${horas} h`;
  const dias = Math.floor(horas / 24);
  const horasRest = horas % 24;
  return horasRest ? `${dias} d ${horasRest} h` : `${dias} d`;
}

/** Solo el nombre de pila usable en plantilla (omite Particular/Contacto/etc.). */
function nombrePilaTicket(usuarioNombre) {
  return nombreParaPlantillaMensaje(usuarioNombre);
}

function etiquetarTicket(detalle) {
  if (!detalle) return "";
  const id = detalle.id != null ? `#${detalle.id}` : "";
  const titulo = String(detalle.asunto || "").trim();
  return [id, titulo].filter(Boolean).join(" ");
}

const RESPUESTAS_RAPIDAS = [
  {
    id: "resuelto",
    label: "Resuelto",
    build: (d) => {
      const nombre = nombrePilaTicket(d.usuario_nombre);
      const saludo = nombre ? `Hola ${nombre}` : "Hola";
      return `${saludo}, ya está resuelto tu ticket ${etiquetarTicket(d)}`;
    },
  },
  {
    id: "en_proceso",
    label: "En proceso",
    build: (d) => {
      const nombre = nombrePilaTicket(d.usuario_nombre);
      const saludo = nombre ? `Hola ${nombre}` : "Hola";
      return `${saludo}, estamos revisando tu ticket ${etiquetarTicket(d)}. Te avisamos en cuanto tengamos novedades.`;
    },
  },
  {
    id: "mas_info",
    label: "Más info",
    build: (d) => {
      const nombre = nombrePilaTicket(d.usuario_nombre);
      const saludo = nombre ? `Hola ${nombre}` : "Hola";
      return `${saludo}, para avanzar con el ticket ${etiquetarTicket(d)} necesitamos un poco más de detalle. ¿Puedes ampliarnos la información?`;
    },
  },
  {
    id: "cerrado",
    label: "Cerrado",
    build: (d) => {
      const nombre = nombrePilaTicket(d.usuario_nombre);
      const saludo = nombre ? `Hola ${nombre}` : "Hola";
      return `${saludo}, damos por cerrado el ticket ${etiquetarTicket(d)}. Si vuelve a ocurrir, abre uno nuevo. ¡Gracias!`;
    },
  },
];

const FILTROS_LISTA = [
  { id: "activos", label: "Activos" },
  { id: "resuelto", label: "Resueltos" },
  { id: "cerrado", label: "Cerrados" },
  { id: "", label: "Todos" },
];

export default function TicketsSoporte() {
  const { theme } = useTheme();
  const isDark = theme.name === "dark";
  const t = theme.colors;
  const [searchParams, setSearchParams] = useSearchParams();

  const bgPage = isDark ? "#0a0c12" : "#f1f5f9";
  const bgCard = isDark ? "#12151e" : "#ffffff";
  const border = isDark ? "#252d42" : "#e2e8f0";
  const txtMain = isDark ? "#f1f5f9" : "#1e293b";
  const txtMuted = isDark ? "#94a3b8" : "#64748b";
  const bgInput = isDark ? "#161b28" : "#f8fafc";

  const [tickets, setTickets] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  // Por defecto solo abiertos/en proceso; Resueltos se abren igual con su filtro.
  const [filtroEstado, setFiltroEstado] = useState("activos");

  const [ticketId, setTicketId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [archivosRespuesta, setArchivosRespuesta] = useState([]);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const inputRespuestaRef = useRef(null);
  const abiertoPorUrlRef = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await api.get("/tickets-soporte", {
        params: filtroEstado ? { estado: filtroEstado } : {},
      });
      setTickets(data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudieron cargar los tickets");
    } finally {
      setCargando(false);
    }
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirTicket = useCallback(async (id, { syncUrl = true } = {}) => {
    const numId = Number(id);
    if (!Number.isFinite(numId) || numId <= 0) return;
    abiertoPorUrlRef.current = numId;
    setTicketId(numId);
    setDetalle(null);
    setCargandoDetalle(true);
    if (syncUrl) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("id", String(numId));
        return next;
      }, { replace: true });
    }
    try {
      const { data } = await api.get(`/tickets-soporte/${numId}`);
      setDetalle(data?.data || null);
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || "No se pudo cargar el ticket");
      setTicketId(null);
      abiertoPorUrlRef.current = null;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("id");
        return next;
      }, { replace: true });
    } finally {
      setCargandoDetalle(false);
    }
  }, [setSearchParams]);

  // Deep link /soporte/tickets?id=53 — también para resueltos/cerrados
  useEffect(() => {
    const raw = searchParams.get("id");
    if (!raw) return undefined;
    const numId = Number(raw);
    if (!Number.isFinite(numId) || numId <= 0) return undefined;
    if (abiertoPorUrlRef.current === numId) return undefined;
    void abrirTicket(numId, { syncUrl: false });
    return undefined;
  }, [searchParams, abrirTicket]);

  const cerrarDetalle = () => {
    setTicketId(null);
    setDetalle(null);
    setNuevoComentario("");
    abiertoPorUrlRef.current = null;
    archivosRespuesta.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    setArchivosRespuesta([]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      return next;
    }, { replace: true });
  };

  const agregarArchivosRespuesta = (fileList) => {
    const nuevos = Array.from(fileList || []);
    if (!nuevos.length) return;
    setArchivosRespuesta((prev) => {
      const disponibles = MAX_ARCHIVOS_RESPUESTA - prev.length;
      if (disponibles <= 0) {
        dialogs.alert(`Solo se permiten hasta ${MAX_ARCHIVOS_RESPUESTA} archivos adjuntos`);
        return prev;
      }
      const aceptados = [];
      const rechazados = [];
      for (const f of nuevos.slice(0, disponibles)) {
        if (f.size > MAX_MB_POR_ARCHIVO * 1024 * 1024) { rechazados.push(f.name); continue; }
        aceptados.push({ file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null });
      }
      if (rechazados.length) dialogs.alert(`No se añadieron (superan ${MAX_MB_POR_ARCHIVO} MB): ${rechazados.join(", ")}`);
      return [...prev, ...aceptados];
    });
  };

  const quitarArchivoRespuesta = (idx) => {
    setArchivosRespuesta((prev) => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const cambiarEstado = async (nuevoEstado) => {
    if (!detalle) return;
    setGuardandoEstado(true);
    try {
      const { data } = await api.patch(`/tickets-soporte/${detalle.id}`, { estado: nuevoEstado });
      const row = data?.data || {};
      setDetalle((d) => ({
        ...d,
        estado: row.estado || nuevoEstado,
        resolved_at: row.resolved_at !== undefined ? row.resolved_at : d.resolved_at,
        updated_at: row.updated_at || d.updated_at,
      }));
      setTickets((list) => list.map((tk) => (
        tk.id === detalle.id
          ? {
              ...tk,
              estado: row.estado || nuevoEstado,
              resolved_at: row.resolved_at !== undefined ? row.resolved_at : tk.resolved_at,
              updated_at: row.updated_at || tk.updated_at,
            }
          : tk
      )));
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || "No se pudo cambiar el estado");
    } finally {
      setGuardandoEstado(false);
    }
  };

  const cambiarPrioridad = async (nuevaPrioridad) => {
    if (!detalle) return;
    try {
      const { data } = await api.patch(`/tickets-soporte/${detalle.id}`, { prioridad: nuevaPrioridad });
      setDetalle((d) => ({ ...d, prioridad: data?.data?.prioridad || nuevaPrioridad }));
      setTickets((list) => list.map((tk) => (tk.id === detalle.id ? { ...tk, prioridad: data?.data?.prioridad || nuevaPrioridad } : tk)));
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || "No se pudo cambiar la prioridad");
    }
  };

  const enviarComentario = async () => {
    if ((!nuevoComentario.trim() && !archivosRespuesta.length) || !detalle) return;
    setEnviandoComentario(true);
    try {
      let data;
      if (archivosRespuesta.length) {
        // Con adjuntos: multipart directo a Railway (Vercel corta multipart → 502).
        const fd = new FormData();
        fd.append("mensaje", nuevoComentario.trim());
        archivosRespuesta.forEach((a) => fd.append("adjuntos", a.file));
        const res = await postCaptacionMultipart(`/tickets-soporte/${detalle.id}/comentarios`, fd);
        data = res.data;
      } else {
        // Solo texto: mismo origen /api (como cambiar estado). Evita fallos CORS/red al host Railway.
        const res = await api.post(`/tickets-soporte/${detalle.id}/comentarios`, {
          mensaje: nuevoComentario.trim(),
        });
        data = res.data;
      }
      setDetalle((d) => ({ ...d, comentarios: [...(d.comentarios || []), data.data] }));
      setNuevoComentario("");
      archivosRespuesta.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      setArchivosRespuesta([]);
      cargar();
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        (e?.code === "ERR_NETWORK" ? "Error de red al contactar el servidor" : null) ||
        "No se pudo enviar la respuesta";
      await dialogs.alert(msg);
    } finally {
      setEnviandoComentario(false);
    }
  };

  const selectSt = {
    padding: "7px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    background: bgInput,
    border: `1.5px solid ${border}`,
    color: txtMain,
    outline: "none",
    fontFamily: "inherit",
  };

  return (
    <div style={{ minHeight: "100%", background: bgPage, padding: 24, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: txtMain, display: "flex", alignItems: "center", gap: 10 }}>
              🎫 Tickets de soporte
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: txtMuted }}>
              Incidencias y solicitudes enviadas desde el icono de soporte del CRM
            </p>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {FILTROS_LISTA.map((f) => {
              const activo = filtroEstado === f.id;
              return (
                <button
                  key={f.id || "todos"}
                  type="button"
                  onClick={() => setFiltroEstado(f.id)}
                  style={{
                    padding: "7px 12px",
                    borderRadius: 999,
                    border: `1.5px solid ${activo ? "#2563eb" : border}`,
                    background: activo ? (isDark ? "rgba(37,99,235,0.2)" : "#eff6ff") : bgCard,
                    color: activo ? (isDark ? "#93c5fd" : "#1d4ed8") : txtMain,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div style={{ padding: 12, borderRadius: 10, background: "#fee2e2", color: "#b91c1c", fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: 14, overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 180px)" }}>
          {cargando ? (
            <div style={{ padding: 40, textAlign: "center", color: txtMuted, fontSize: 13 }}>Cargando tickets…</div>
          ) : tickets.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: txtMuted, fontSize: 13 }}>
              No hay tickets{filtroEstado === "activos" ? " activos" : filtroEstado ? ` en estado "${ESTADO_LABEL[filtroEstado] || filtroEstado}"` : ""}.
              {filtroEstado === "activos" ? " Usa «Resueltos» o «Todos» para ver el historial." : ""}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 920 }}>
              <thead>
                <tr style={{ background: isDark ? "#0d1018" : "#f8fafc", borderBottom: `1px solid ${border}` }}>
                  {["#", "Fecha creación", "Fecha resuelto", "Usuario", "Asunto", "Categoría", "Prioridad", "Estado", ""].map((h) => (
                    <th key={h || "acciones"} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: txtMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map((tk) => {
                  const ec = ESTADO_COLOR[tk.estado] || ESTADO_COLOR.abierto;
                  const dur = duracionResolucion(tk.created_at, tk.resolved_at);
                  return (
                    <tr
                      key={tk.id}
                      onClick={() => abrirTicket(tk.id)}
                      style={{ borderBottom: `1px solid ${border}`, cursor: "pointer" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.03)" : "#f8fafc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ padding: "10px 14px", color: txtMuted }}>#{tk.id}</td>
                      <td style={{ padding: "10px 14px", color: txtMuted, whiteSpace: "nowrap" }}>{fechaCorta(tk.created_at)}</td>
                      <td style={{ padding: "10px 14px", color: txtMuted, whiteSpace: "nowrap" }}>
                        {tk.resolved_at ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span>{fechaCorta(tk.resolved_at)}</span>
                            {dur ? <span style={{ fontSize: 11, fontWeight: 700, color: txtMain }}>{dur}</span> : null}
                          </div>
                        ) : "—"}
                      </td>
                      <td style={{ padding: "10px 14px", color: txtMain }}>{tk.usuario_nombre || "—"}</td>
                      <td style={{ padding: "10px 14px", color: txtMain, fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tk.asunto}</td>
                      <td style={{ padding: "10px 14px", color: txtMuted }}>{CATEGORIA_LABEL[tk.categoria] || tk.categoria}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ color: PRIORIDAD_COLOR[tk.prioridad] || txtMuted, fontWeight: 700 }}>{PRIORIDAD_LABEL[tk.prioridad] || tk.prioridad}</span>
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: ec.bg, color: ec.fg }}>{ESTADO_LABEL[tk.estado] || tk.estado}</span>
                      </td>
                      <td style={{ padding: "10px 14px", color: txtMuted, fontSize: 12, whiteSpace: "nowrap" }}>
                        {tk.num_comentarios > 0 ? `💬 ${tk.num_comentarios} ` : ""}
                        {tk.num_adjuntos > 0 ? `📎 ${tk.num_adjuntos}` : ""}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); abrirTicket(tk.id); }}
                          style={{
                            marginLeft: 8,
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: `1px solid ${border}`,
                            background: isDark ? "#1a2234" : "#f8fafc",
                            color: txtMain,
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {ticketId != null && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10050 }}
          onClick={cerrarDetalle}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: "94vw", maxHeight: "90vh", display: "flex", flexDirection: "column", background: bgCard, border: `1px solid ${border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.4)" }}
          >
            {cargandoDetalle || !detalle ? (
              <div style={{ padding: 40, textAlign: "center", color: txtMuted }}>Cargando…</div>
            ) : (
              <>
                <div style={{ padding: "18px 22px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: `1px solid ${border}`, gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 11, color: txtMuted, fontWeight: 700 }}>TICKET #{detalle.id}</p>
                    <h2 style={{ margin: "3px 0 0", fontSize: 16, fontWeight: 800, color: txtMain }}>{detalle.asunto}</h2>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: txtMuted }}>
                      {detalle.usuario_nombre || "—"} · {detalle.usuario_email || "sin email"} · {fechaCorta(detalle.created_at)}
                    </p>
                  </div>
                  <button onClick={cerrarDetalle} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${border}`, background: isDark ? "#1f2937" : "#f3f4f6", color: txtMuted, cursor: "pointer", flexShrink: 0 }}>✕</button>
                </div>

                <div style={{ padding: "14px 22px", display: "flex", gap: 10, flexWrap: "wrap", borderBottom: `1px solid ${border}` }}>
                  <select value={detalle.estado} disabled={guardandoEstado} onChange={(e) => cambiarEstado(e.target.value)} style={selectSt}>
                    {Object.entries(ESTADO_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <select value={detalle.prioridad} onChange={(e) => cambiarPrioridad(e.target.value)} style={selectSt}>
                    {Object.entries(PRIORIDAD_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                  <span style={{ ...selectSt, background: "transparent", border: "none", color: txtMuted }}>
                    {CATEGORIA_LABEL[detalle.categoria] || detalle.categoria}
                  </span>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
                  <p style={{ margin: "0 0 8px", fontSize: 13, color: txtMain, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{detalle.descripcion}</p>
                  <AdjuntosGrid adjuntos={detalle.adjuntos} border={border} txtMuted={txtMuted} isDark={isDark} />
                  {detalle.pagina_url && (
                    <p style={{ margin: "0 0 18px", fontSize: 12, color: txtMuted }}>Página: {detalle.pagina_url}</p>
                  )}

                  {(detalle.comentarios || []).length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
                      {detalle.comentarios.map((c) => (
                        <div key={c.id} style={{ background: bgInput, border: `1px solid ${border}`, borderRadius: 10, padding: "10px 12px" }}>
                          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: txtMuted }}>{c.usuario_nombre || "—"} · {fechaCorta(c.created_at)}</p>
                          <p style={{ margin: "4px 0 0", fontSize: 13, color: txtMain, whiteSpace: "pre-wrap" }}>{c.mensaje}</p>
                          <AdjuntosGrid adjuntos={c.adjuntos} border={border} txtMuted={txtMuted} isDark={isDark} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ padding: "14px 22px 18px", borderTop: `1px solid ${border}`, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: txtMuted, alignSelf: "center", marginRight: 2 }}>
                      Rápidas:
                    </span>
                    {RESPUESTAS_RAPIDAS.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        title={r.build(detalle)}
                        onClick={() => setNuevoComentario(r.build(detalle))}
                        style={{
                          padding: "5px 10px",
                          borderRadius: 999,
                          border: `1px solid ${border}`,
                          background: isDark ? "#1a2234" : "#f8fafc",
                          color: txtMain,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={nuevoComentario}
                    onChange={(e) => setNuevoComentario(e.target.value)}
                    placeholder="Escribe una respuesta…"
                    style={{ width: "100%", minHeight: 70, padding: "9px 12px", borderRadius: 8, fontSize: 13, background: bgInput, border: `1.5px solid ${border}`, color: txtMain, outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
                  />
                  <input
                    ref={inputRespuestaRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    style={{ display: "none" }}
                    onChange={(e) => { agregarArchivosRespuesta(e.target.files); e.target.value = ""; }}
                  />
                  {archivosRespuesta.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {archivosRespuesta.map((a, idx) => (
                        <div key={idx} style={{ position: "relative", width: 56, height: 56, borderRadius: 8, overflow: "hidden", border: `1px solid ${border}`, background: isDark ? "#111827" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }} title={a.file.name}>
                          {a.preview ? (
                            <img src={a.preview} alt={a.file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <span style={{ fontSize: 18 }}>{a.file.type === "application/pdf" ? "📄" : "📎"}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => quitarArchivoRespuesta(idx)}
                            style={{ position: "absolute", top: 1, right: 1, width: 16, height: 16, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.65)", color: "#fff", fontSize: 10, lineHeight: "16px", cursor: "pointer", padding: 0 }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <button
                      type="button"
                      onClick={() => inputRespuestaRef.current?.click()}
                      disabled={archivosRespuesta.length >= MAX_ARCHIVOS_RESPUESTA}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: `1.5px dashed ${isDark ? "#374151" : "#cbd5e1"}`,
                        background: "transparent",
                        color: txtMuted,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      📎 Adjuntar
                    </button>
                    <button
                      onClick={enviarComentario}
                      disabled={enviandoComentario || (!nuevoComentario.trim() && !archivosRespuesta.length)}
                      style={{
                        padding: "10px 24px",
                        borderRadius: 10,
                        border: "none",
                        background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                        color: "#fff",
                        cursor: enviandoComentario ? "wait" : "pointer",
                        fontFamily: "inherit",
                        fontSize: 13,
                        fontWeight: 700,
                        opacity: (!nuevoComentario.trim() && !archivosRespuesta.length) ? 0.6 : 1,
                      }}
                    >
                      {enviandoComentario ? "Enviando…" : "Responder"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
